import React from "react";
import { useSearchForm } from "./hooks";
import { useIsMobile } from "../../hooks/useIsMobile";
import SearchInput from "./components/SearchInput";
import FilterToggle from "./components/FilterToggle";
import PopulationFilters from "./components/PopulationFilters";
import AdvancedFilters from "./components/AdvancedFilters";
import ResultList from "../ResultList/ResultList";
import ViewDetail from "../ViewDetail/ViewDetail";
import CollapseButton from "./components/CollapseButton";
import { POPULATION_DATA, SERVICE_DATA } from "../../shared/population-colors";

interface SearchFormProps {
  isOpen?: boolean;
  onRequestClose?: () => void;
  panelHeight?: number;
}

const ACCENT = "#ce0047";
const PANEL_W = 560;

/* ---------------- Chips (píldoras) de filtros activos ---------------- */
type Chip = { key: string; label: string };

const ActiveFiltersChips: React.FC<{
  chips: Chip[];
  onRemove: (key: string) => void;
  onClearAll?: () => void;
  isMobile?: boolean;
}> = ({ chips, onRemove, onClearAll, isMobile }) => {
  if (!chips.length) return null;

  return (
    <div className="px-4 pb-2">
      <div className="flex flex-wrap gap-2 items-center">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => onRemove(c.key)}
            className={`flex items-center gap-1 bg-primary/10 text-primary ${
              isMobile
                ? "text-xs px-1.5 py-0.5"
                : "text-xs px-2 py-1"
            } rounded-full hover:bg-gray-200`}
          >
            <span>{c.label}</span>
            <span className="material-icons-outlined text-[16px]">close</span>
          </button>
        ))}
      </div>
    </div>
  );
};
/* -------------------------------------------------------------------- */

const SearchForm: React.FC<SearchFormProps> = ({
  isOpen = true,
  onRequestClose,
  panelHeight,
}) => {
  const isMobile = useIsMobile();
  const {
    item,
    isListOpen,
    isSearchResultOpen,
    isFiltersOpen,
    isItemDetailOpen,
    minimizeViewMobile,
    isAdvancedFiltersExpanded,
    daysFilter,
    populationFilter,
    serviceFilter,
    searchInput,
    searchResult,
    noSearchResult,
    hasResults,
    hideSearchResults,
    toggleMinimizeViewMobile,
    closeMinimizeViewMobile,
    toggleFilter,
    toggleAdvancedFilters,
    openList,
    closeList,
    openItemDetail,
    closeItemDetail,
    goHome,
    onSearch,
    selectSearchResult,
    clearInput,
    clearFilters,
    selectPopulation,
    selectService,
    selectDays,
    applyFilter,
    setSelectedItem,
    generatePdf,
    copyUrl,
  } = useSearchForm();

  const filtersCount =
    populationFilter.length + serviceFilter.length + daysFilter.length;
  const hasFiltersApplied =
    populationFilter.length > 0 ||
    serviceFilter.length > 0 ||
    daysFilter.length > 0;

  const getPopulationLabel = (name: string) => {
    const population = Object.values(POPULATION_DATA).find(
      (p) => p.name === name
    );
    return population?.label || name;
  };

  const getServiceLabel = (name: string) => {
    const service = Object.values(SERVICE_DATA).find((s) => s.name === name);
    return service?.label || name;
  };

  const chips: Chip[] = [
    ...populationFilter.map((p) => ({
      key: p,
      label: getPopulationLabel(p),
    })),
    ...serviceFilter.map((s) => ({ key: s, label: getServiceLabel(s) })),
    ...daysFilter.map((d) => ({ key: d, label: d })),
  ];

  const panelWidthPx = PANEL_W;

  return (
    <div className="relative pointer-events-none">
      {/* Botón de colapsar/expandir con estilo DANE */}
      <CollapseButton
        isOpen={isOpen}
        panelWidthPx={panelWidthPx}
        onToggle={() => onRequestClose?.()}
        positioning="absolute"
      />

      {/* Panel deslizante */}
      <div
        className={[
          "will-change-transform transition-all duration-300",
          isOpen
            ? "translate-x-0 opacity-100 pointer-events-auto"
            : "-translate-x-[calc(100%+16px)] opacity-0 pointer-events-none",
          "w-[92vw] max-w-[560px] md:w-[560px]",
        ].join(" ")}
      >
        <div
          className="rounded-2xl shadow-2xl bg-white border border-gray-300"
          style={{
            height: panelHeight
              ? `${panelHeight}px`
              : isListOpen && !isFiltersOpen
              ? "80vh"
              : "fit-content",
          }}
        >
          <div
            className={`${
              isListOpen && !isFiltersOpen ? "h-full" : "h-auto"
            } overflow-y-auto no-scrollbar`}
          >
            {(isItemDetailOpen || isListOpen || isFiltersOpen) && (
              <button
                className="w-full flex justify-center items-center pt-3 md:hidden pointer-events-auto"
                onClick={toggleMinimizeViewMobile}
              >
                <span className="block w-1/3 h-1 bg-gray-500 rounded-full"></span>
              </button>
            )}

            {!isItemDetailOpen && (
              <div className="h-fit md:mt-0 px-2 md:px-4 pt-2 pb-4 mt-4">
                {/* Barra de búsqueda */}
                <div className="relative flex flex-row items-center gap-2 w-full min-h-[44px] mb-4">
                  <div className="flex-1 min-w-0">
                    <SearchInput
                      searchInput={searchInput}
                      searchResult={searchResult}
                      noSearchResult={noSearchResult}
                      isSearchResultOpen={isSearchResultOpen}
                      onSearch={onSearch}
                      onSelectSearchResult={selectSearchResult}
                      onFocus={closeMinimizeViewMobile}
                      onBlur={hideSearchResults}
                    />
                  </div>

                  <div className="flex gap-x-2 flex-shrink-0">
                    <FilterToggle
                      isFiltersOpen={isFiltersOpen}
                      filtersCount={filtersCount}
                      onToggle={toggleFilter}
                      onShowList={openList}
                      hasFiltersApplied={hasFiltersApplied}
                      isListOpen={isListOpen}
                      hasResults={hasResults}
                    />
                  </div>
                </div>

                {/* Filtros */}
                {!isListOpen && (
                  <>
                    <PopulationFilters
                      populationFilter={populationFilter}
                      onPopulationChange={selectPopulation}
                    />
                    <AdvancedFilters
                      serviceFilter={serviceFilter}
                      daysFilter={daysFilter}
                      onServiceChange={selectService}
                      onDaysChange={selectDays}
                    />
                    
                    {/* Botón para aplicar filtros */}
                    {(populationFilter.length > 0 || serviceFilter.length > 0 || daysFilter.length > 0) && (
                      <div className="px-4 py-3 border-t border-gray-200 flex justify-center">
                        <button
                          onClick={openList}
                          className="w-full bg-accent text-white py-2 px-4 rounded-lg text-base font-normal hover:bg-accent/90 transition-colors"
                        >
                          Mostrar resultados
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Chips de filtros activos */}
                {isListOpen && !isFiltersOpen && (
                  <ActiveFiltersChips
                    chips={chips}
                    onRemove={(k) => {
                      if (populationFilter.includes(k)) selectPopulation(k);
                      else if (serviceFilter.includes(k)) selectService(k);
                      else if (daysFilter.includes(k)) selectDays(k);
                    }}
                    onClearAll={clearFilters}
                    isMobile={isMobile}
                  />
                )}
              </div>
            )}

            {isListOpen && !isFiltersOpen && (
              <ResultList
                onCloseList={closeList}
                onOpenDetail={openItemDetail}
                onShareItem={copyUrl}
                onDownloadItem={generatePdf}
                buildPopulation={(s) => buildPopulation(s)}
                buildService={(s) => buildService(s)}
                capitalizarPrimeraLetra={capitalizarPrimeraLetra}
              />
            )}

            {isItemDetailOpen && item && (
              <ViewDetail
                item={item}
                onClose={closeItemDetail}
                onGeneratePdf={generatePdf}
                onCopyUrl={copyUrl}
                capitalizarPrimeraLetra={capitalizarPrimeraLetra}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Helpers ---------------- */
function buildPopulation(population: string) {
  const name = (population || "").toLowerCase();
  const arr: { name: string; color: string; icon: string; label: string }[] = [];
  if (name.includes("mayores")) arr.push(POPULATION_DATA["Personas Mayores"]);
  if (name.includes("niños"))
    arr.push(POPULATION_DATA["Niños, Niñas y/o Adolescentes"]);
  if (name.includes("discapacidad"))
    arr.push(POPULATION_DATA["Personas con Discapacidad"]);
  if (name.includes("cuidador"))
    arr.push(POPULATION_DATA["Cuidadores"]);
  return arr;
}

function buildService(service: string) {
  const s = (service || "").toLowerCase();
  const arr: { name: string; label: string }[] = [];
  if (s.includes("centro de desarrollo infantil"))
    arr.push(SERVICE_DATA["Centro de Desarrollo Infantil"]);
  if (s.includes("jardín") || s.includes("guardería"))
    arr.push(SERVICE_DATA["Jardín / Guardería Infantil"]);
  if (s.includes("escuela") || s.includes("colegio"))
    arr.push(SERVICE_DATA["Escuela o Colegio"]);
  if (s.includes("limpieza"))
    arr.push(SERVICE_DATA["Relevos en la limpieza de espacios"]);
  if (s.includes("día"))
    arr.push(SERVICE_DATA["Cuidado durante el día - Centro Día"]);
  if (s.includes("lavandería"))
    arr.push(SERVICE_DATA["Lavandería"]);
  if (s.includes("residencia"))
    arr.push(SERVICE_DATA["Residencia"]);
  if (s.includes("comedor"))
    arr.push(SERVICE_DATA["Comedor Comunitario"]);
  if (s.includes("transporte de poblaciones"))
    arr.push(SERVICE_DATA["Transporte de poblaciones específicas"]);
  if (arr.length === 0) arr.push(SERVICE_DATA["Otros"]);
  return arr;
}

function capitalizarPrimeraLetra(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export default SearchForm;
